// Markets: the terrain. TAM/ASP now, complexity expectation, rivals in the
// field (M3: the pressure on your share), your products & EOL. Segment markets
// (P1: one industry, attr-weighted segments) add the tech gate, what the
// segment demands, and each product's archFit vs. the moving tree frontier —
// tooltip-with-formula, per the transparency principle.

import type { ReactElement } from "react";
import type { MarketDef, Product, RivalDef } from "../../engine/types.js";
import { ATTR_KEYS } from "../../engine/types.js";
import { unitCost, dieArea } from "../../engine/formulas/yieldModel.js";
import { rivalDefOf, rivalStrengthIn } from "../../engine/systems/rivals.js";
import { rivalAcquirable, rivalIsDistressed, rivalPrice } from "../../engine/systems/ma.js";
import { archFitFactor, productAttrs } from "../../engine/systems/market.js";
import { frontierAttrs } from "../../engine/systems/tree.js";
import { interpolate } from "../../engine/calendar.js";
import { stageAtLeast } from "../../engine/types.js";
import { activeTreeOf, expectedKGatesAt, marketColor, refAspAt, tamAt, viewCtx, type ScreenProps } from "./shared.js";
import { money, num, pct } from "../format.js";
import { DieRect, Sparkline } from "../components/widgets.js";

/** Live attr weight for a segment dimension (era curves supersede statics). */
function weightAt(mk: MarketDef, k: (typeof ATTR_KEYS)[number], m: number): number {
  const curve = mk.attrWeightCurves?.[k];
  return curve ? interpolate(curve, m) : (mk.attrWeights?.[k] ?? 0);
}

export function Markets({ snap, content, session }: ScreenProps) {
  const { view, pending } = snap;
  const m = view.monthIndex;
  const segmented = content.markets.some((mk) => mk.attrWeights);

  // rivals present per market this month (public intel, mirrors the engine)
  const rivalsIn = (marketId: string): { def: RivalDef; strength: number; id: string }[] => {
    const out: { def: RivalDef; strength: number; id: string }[] = [];
    for (const id of Object.keys(view.rivals).sort()) {
      const st = view.rivals[id]!;
      if (!st.alive) continue;
      const def = rivalDefOf(content, view.rivals, id);
      const strength = rivalStrengthIn(def, st, marketId, m);
      if (strength > 0) out.push({ def, strength, id });
    }
    return out.sort((a, b) => b.strength - a.strength);
  };

  // segment tech gates read the TREE, not the fab nodes
  const gateOpen = (mk: MarketDef): boolean =>
    !mk.techGatesAnyOf || mk.techGatesAnyOf.some((id) => id in view.tree.researched);
  const tree = activeTreeOf(content);

  return (
    <div>
      <h2>{segmented ? "THE MARKET — ONE INDUSTRY, MANY SEGMENTS" : "MARKETS"}</h2>
      {content.markets.map((mk) => {
        const products = view.products.filter((p) => p.marketId === mk.id);
        const tam = tamAt(mk, m);
        const share = view.markets[mk.id]?.totalShare ?? 0;
        const onPath = view.path === null || mk.pathAffinity === "neutral" || mk.pathAffinity === view.path;
        const rivals = rivalsIn(mk.id);
        const rivalStrength = rivals.reduce((a, r) => a + r.strength, 0);
        const pressure = Math.min(
          content.economy.models.rivalPressureMax,
          Math.max(content.economy.models.rivalPressureMin, (1 + mk.rivalryRef) / (1 + rivalStrength)),
        );
        const customerRel = view.stakeholders.customers[mk.id] ?? 55;
        if (tam < 1000 && products.length === 0 && rivals.length === 0) return null; // not a market yet
        const isSegment = !!mk.attrWeights;
        const demands = isSegment
          ? ATTR_KEYS.map((k) => ({ k, w: weightAt(mk, k, m) }))
              .filter((x) => x.w > 0)
              .sort((a, b) => b.w - a.w)
          : [];
        return (
          <div className="panel" key={mk.id}>
            <div className="title">
              {mk.displayName.toUpperCase()}{" "}
              {isSegment ? (
                <span className="badge cyan">SEGMENT</span>
              ) : (
                <span className={`badge ${onPath ? "cyan" : "amber"}`}>{mk.pathAffinity.toUpperCase()}</span>
              )}
              {!onPath && <span className="hint"> off your path — chips here are taxed</span>}
              {isSegment && !gateOpen(mk) && (
                <span className="warn-text">
                  {" "}
                  TECH-GATED — needs {mk.techGatesAnyOf!.map((id) => tree?.nodes.find((n) => n.id === id)?.displayName ?? id).join(" or ")} (TREE screen)
                </span>
              )}
            </div>
            {isSegment && demands.length > 0 && (
              <div className="hint" style={{ marginBottom: 6 }}>
                what this segment buys on:{" "}
                {demands.map(({ k, w }) => `${k} ×${w.toFixed(1)}`).join(" · ")} — weights move with the era; archFit scores your architecture
                against the frontier on exactly these
              </div>
            )}
            <div className="row">
              <div className="tile">
                <div className="k">TAM NOW</div>
                <div className="v">{num(tam)}/mo</div>
                <div className="sub">units per month</div>
              </div>
              <div className="tile">
                <div className="k">REFERENCE ASP</div>
                <div className="v">${refAspAt(mk, m).toFixed(0)}</div>
                <div className="sub">era price for a well-executed part</div>
              </div>
              <div className="tile">
                <div className="k">MARKET EXPECTS</div>
                <div className="v">{num(expectedKGatesAt(mk, m))}kG</div>
                <div className="sub">complexity doubles every {mk.expectedKGatesDoublingMonths}mo</div>
              </div>
              <div className="tile">
                <div className="k">YOUR SHARE</div>
                <div className="v">{pct(share)}</div>
                <Sparkline data={view.kpiHistory.slice(-48).map((k) => k.shareByMarket[mk.id] ?? 0)} color={marketColor(mk.id)} height={20} />
                <div className="sub">cap {pct(mk.totalShareCap, 0)} · customers {customerRel.toFixed(0)}/100</div>
              </div>
              <div className="tile">
                <div className="k">RIVAL PRESSURE</div>
                <div className={`v${pressure < 0.9 ? " warn" : pressure > 1.1 ? " good" : ""}`}>
                  ×{pressure.toFixed(2)}
                  {pressure > 1.1 ? " ▲" : pressure < 0.9 ? " ▼" : ""}
                </div>
                <div className="sub">
                  {rivals.length} rival{rivals.length === 1 ? "" : "s"} ·{" "}
                  {pressure > 1.1
                    ? "thin field lifts your share & price"
                    : pressure < 0.9
                      ? "crowded field cuts your share & price"
                      : "multiplies your share & price"}
                </div>
              </div>
            </div>
            {rivals.length > 0 && (
              <div className="pending-list">
                {rivals.map(({ def, strength, id }) => {
                  const acquirable = rivalAcquirable(content, view.rivals, id, m) && stageAtLeast(view.stage, content.economy.ma.minStageToBuy);
                  const distressed = rivalIsDistressed(content, view.rivals, id, m);
                  const price = acquirable ? rivalPrice(content, view.rivals, id, m) : 0;
                  const buyQueued = pending.some((c) => c.type === "acquireRival" && c.rivalId === id);
                  return (
                    <div key={id} className="p">
                      <span>
                        ▪ {def.displayName} <span className="dim">({def.archetype})</span> — presence {strength.toFixed(2)}
                        {distressed && <span className="bad-text"> · STRUGGLING</span>}
                      </span>
                      {acquirable && (
                        <span style={{ display: "flex", gap: 6 }}>
                          <button
                            className={`act${buyQueued ? " queued" : ""}`}
                            onClick={() => session.queue({ type: "acquireRival", rivalId: id, payment: "cash" })}
                            title={`bundle: their team, their sockets, an integration-risk window`}
                          >
                            {buyQueued ? "DEAL QUEUED" : `ACQUIRE ~${money(price)} CASH`}
                          </button>
                          {view.stage === "public" && (
                            <button className="act" onClick={() => session.queue({ type: "acquireRival", rivalId: id, payment: "stock" })}>
                              STOCK
                            </button>
                          )}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {products.some((p) => p.active) && (
              <ChannelControl marketId={mk.id} snap={snap} content={content} session={session} />
            )}
            {products.length > 0 && (
              <div className="scroll-x">
                <table>
                  <thead>
                    <tr>
                      <th>product</th>
                      <th>node</th>
                      <th className="r">kG</th>
                      <th>die</th>
                      <th className="r">age</th>
                      {isSegment && <th className="r">archFit</th>}
                      <th className="r">share</th>
                      <th className="r">quality</th>
                      <th className="r">sell-in/mo</th>
                      <th className="r">sell-thru</th>
                      <th className="r">channel</th>
                      <th className="r">ASP</th>
                      <th className="r">unit cost</th>
                      <th className="r">gross/mo</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p) => (
                      <ProductRow key={p.id} p={p} snap={snap} content={content} session={session} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {products.length === 0 && <span className="hint">no products here yet</span>}
          </div>
        );
      })}
      <span className="hint">
        Share is recomputed monthly from execution quality × complexity currency × node currency. Old products decay — end-of-life them
        before they turn into negative-margin volume.
      </span>
    </div>
  );
}

/** Channel policy dial (M2, design §10): months of forward demand held in
 *  distribution. Sell-in books revenue early; price protection bleeds while
 *  ASPs erode; downturns cancel your orders while the channel drains. */
function ChannelControl({ marketId, snap, content, session }: ScreenProps & { marketId: string }) {
  const { view, pending } = snap;
  const current = view.markets[marketId]?.channelMonths ?? 0;
  const queued = pending.find((c) => c.type === "setChannelPolicy" && c.marketId === marketId);
  const shown = queued && queued.type === "setChannelPolicy" ? queued.months : current;
  const maxMonths = content.economy.channel.maxMonths;
  const set = (v: number) => session.queue({ type: "setChannelPolicy", marketId, months: Math.max(0, Math.min(maxMonths, v)) });
  const channelTotal = view.products.filter((p) => p.marketId === marketId).reduce((a, p) => a + p.channelUnits, 0);
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
      <span className="k">CHANNEL POLICY</span>
      <span className="stepper">
        <button onClick={() => set(shown - 0.5)}>‹</button>
        <span className={`val${queued ? " queued" : ""}`}>{shown.toFixed(1)}mo</span>
        <button onClick={() => set(shown + 0.5)}>›</button>
      </span>
      <span className="hint">
        target months of demand on distributors' shelves (0 = direct only) · in channel now: {num(channelTotal)} units
        {shown > 1 ? " · sell-in runs ahead of sell-through — the 2001 glut wrecks whoever holds this in a downturn" : ""}
      </span>
    </div>
  );
}

function ProductRow({ p, snap, content, session }: ScreenProps & { p: Product }) {
  const { view, report, pending } = snap;
  const m = view.monthIndex;
  const mk = content.markets.find((x) => x.id === p.marketId)!;
  const node = content.nodes.find((n) => n.id === p.nodeId)!;
  const uc = unitCost(node, mk, p.dieAreaMm2 || dieArea(p.kGates, node, content.economy.models), m, view.nodes[p.nodeId]!.researchEm, content.economy.models);
  const eolQueued = pending.some((c) => c.type === "endOfLifeProduct" && c.productId === p.id);
  // last month's actuals from the tick report (undefined pre-sales or when inactive)
  const sale = report?.productSales.find((s) => s.productId === p.id);
  const gross = sale ? sale.revenue - sale.cogs - sale.recall : 0;
  // archFit vs. the moving frontier, with the full formula in the tooltip
  const ctx = viewCtx(view, content);
  const tree = activeTreeOf(content);
  const isSegment = !!mk.attrWeights;
  let fitCell: ReactElement | null = null;
  if (isSegment && tree) {
    const fit = archFitFactor(ctx, mk, p, m);
    const models = content.economy.models;
    const frontier = frontierAttrs(ctx, tree, m);
    const attrs = productAttrs(ctx, p, m);
    const lines = ATTR_KEYS.filter((k) => weightAt(mk, k, m) > 0).map((k) => {
      const ideal = frontier[k] * models.frontierDiscount;
      return `${k}: yours ${attrs[k].toFixed(1)} vs frontier ${ideal.toFixed(1)} (weight ${weightAt(mk, k, m).toFixed(1)})`;
    });
    const tip =
      p.archNodes.length === 0
        ? "no architecture on record — archFit is neutral (×1.00)"
        : `share ×= clamp((Σw·min(yours/frontier, ${models.archRatioCap}))^${models.archFitExp}, ${models.archFitMin}, ${models.archFitMax}); price rides a dampened power of it\n` +
          lines.join("\n");
    fitCell = (
      <td className="r" title={tip} style={fit < 0.85 ? { color: "var(--red)" } : fit > 1.05 ? { color: "var(--cyan)" } : undefined}>
        ×{fit.toFixed(2)}
      </td>
    );
  } else if (isSegment) {
    fitCell = <td className="r">—</td>;
  }
  return (
    <tr className={p.active ? "" : "inactive"}>
      <td>{p.id}</td>
      <td>{node.displayName}</td>
      <td className="r">{num(p.kGates)}</td>
      <td>
        <DieRect dieAreaMm2={p.dieAreaMm2} color={marketColor(p.marketId)} size={20} />
      </td>
      <td className="r">{m - p.releasedMonth}mo</td>
      {fitCell}
      <td className="r">{pct(p.share)}</td>
      <td className="r">{p.qualityExec.toFixed(2)}</td>
      <td className="r">{sale ? num(sale.units) : "—"}</td>
      <td className="r">{sale ? num(sale.endUnits) : "—"}</td>
      <td className="r">{p.channelUnits > 0 ? num(p.channelUnits) : "—"}</td>
      <td className="r">{sale ? `$${sale.asp.toFixed(2)}` : "—"}</td>
      <td className="r">${(sale?.unitCost ?? uc.unitCost).toFixed(2)}</td>
      <td className="r" style={sale && gross < 0 ? { color: "var(--red)" } : undefined}>
        {sale ? money(gross) : "—"}
      </td>
      <td>
        {p.active ? (
          <button className={`act bad${eolQueued ? " queued" : ""}`} onClick={() => session.queue({ type: "endOfLifeProduct", productId: p.id })}>
            {eolQueued ? "EOL QUEUED" : "EOL"}
          </button>
        ) : (
          <span className="dim">retired</span>
        )}
      </td>
    </tr>
  );
}
